import {
  FilterDocument,
  FilterDocumentDateFilter,
  FilterDocumentDurationFilter,
  FilterOptionKeys,
} from '@griply/types';
import { isDefaultFilterDocument } from '@griply/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FilterMinimalStyleCircle18 } from 'shared/assets/icons';
import {
  Button,
  Colors as ButtonColors,
  Sizes,
  Variants,
} from 'shared/components/ui/button';
import { FilterContextMenu } from 'shared/components/ui/filter-context-menu';
import {
  Colors as IconButtonColors,
  IconButton,
} from 'shared/components/ui/icon-button';
import { ButtonVariants } from 'shared/components/ui/popup-menu';
import { FilterDocuments } from 'shared/constants';
import { useFilterState } from 'shared/hooks/use-filter-state';
import { useOnFilterOption } from 'shared/hooks/use-on-filter';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { useUser } from 'shared/hooks/use-user';
import { initialDateFormat } from 'shared/types/date-format-options';
import { FilterButtonOption, FilterOptionType } from 'shared/types/filter';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { GroupByMode, SortingMode } from 'shared/types/sorting-mode';
import { Tag } from 'shared/types/tag';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import * as Styled from './filter-button.style';

enum ExtraFilterOptions {
  CreateSmartList = 'createSmartList',
  UpdateSmartList = 'updateSmartList',
}

export type FilterButtonProps<
  GBM extends GroupByMode,
  SM extends SortingMode,
  F extends FilterOptionKeys,
> = {
  filterDocument: FilterDocument<GBM, SM, F>;
  defaultFilterDocument?: FilterDocument<GBM, SM, F>;
  supportedGroupByModes?: readonly GBM[];
  supportedSortingModes?: readonly SM[];
  supportedFilters?: readonly F[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  updateFilterDocument?: (filterDocument: FilterDocument<GBM, SM, F>) => void;
  onReset: () => void;
  labelOverrides?: Partial<Record<GroupByMode, string>>;
  hideNoGoal?: boolean;
  renderAsIconButton?: boolean;
  onCreateSmartList?: () => void;
  onUpdateSmartList?: () => void;
  preventDrag?: boolean;
};

export const FilterButton = <
  GBM extends GroupByMode,
  SM extends SortingMode,
  F extends FilterOptionKeys,
>({
  filterDocument,
  defaultFilterDocument,
  supportedGroupByModes,
  supportedSortingModes,
  supportedFilters,
  goals,
  lifeAreas,
  tags,
  updateFilterDocument,
  onReset,
  labelOverrides,
  hideNoGoal,
  renderAsIconButton,
  onCreateSmartList,
  onUpdateSmartList,
  preventDrag,
}: FilterButtonProps<GBM, SM, F>) => {
  const filterButtonRef = React.useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const user = useUser();
  const dateFormat = user?.settings?.dateFormat ?? initialDateFormat;
  const weekStartsOn = getWeekStartsOn(user);
  const {
    menuOpen: filterMenuOpen,
    openMenu: openFilterMenu,
    closeMenu: closeFilterMenu,
  } = useOpenMenu();

  const { filters, activeFilters, activeOptions } = useFilterState(
    filterDocument,
    {
      lifeAreas,
      tags,
      goals,
      dateFormat,
      weekStartsOn,
      defaultFilterDocument,
      supportedGroupByModes,
      supportedSortingModes,
      supportedFilters,
      labelOverrides,
      hideNoGoal,
    },
  );

  const onFilter = useOnFilterOption(
    filterDocument,
    defaultFilterDocument,
    updateFilterDocument,
  );

  const _onFilter = (options: {
    id: ID;
    value?: string | string[];
    durationFilter?: FilterDocumentDurationFilter | null;
    dateFilter?: FilterDocumentDateFilter | null;
  }) => {
    if (
      options.id === ExtraFilterOptions.CreateSmartList &&
      onCreateSmartList
    ) {
      closeFilterMenu();
      onCreateSmartList();
      return;
    }

    if (
      options.id === ExtraFilterOptions.UpdateSmartList &&
      onUpdateSmartList
    ) {
      closeFilterMenu();
      onUpdateSmartList();
      return;
    }

    onFilter(options);
  };

  const enhancedFilters = React.useMemo(() => {
    if (!onCreateSmartList && !onUpdateSmartList) {
      return filters;
    }

    return filters.map((group) => {
      if (group.id === FilterDocuments.ResetFilterDocumentID) {
        const extraFilters: FilterButtonOption[] = [];

        if (onCreateSmartList) {
          extraFilters.push({
            id: ExtraFilterOptions.CreateSmartList,
            label: t(
              'context-menus.filtering.task.filters.create-smart-list.label',
            ),
            type: FilterOptionType.Button,
            buttonVariant: ButtonVariants.Outlined,
            disabled: !activeFilters,
          });
        }

        if (onUpdateSmartList) {
          const isDirty = !isDefaultFilterDocument(
            filterDocument,
            defaultFilterDocument,
            supportedFilters,
          );
          extraFilters.push({
            id: ExtraFilterOptions.UpdateSmartList,
            label: t(
              'context-menus.filtering.task.filters.update-smart-list.label',
            ),
            type: FilterOptionType.Button,
            buttonVariant: ButtonVariants.Outlined,
            disabled: !isDirty,
          });
        }

        return {
          ...group,
          filters: [...group.filters, ...extraFilters],
        };
      }
      return group;
    });
  }, [
    onCreateSmartList,
    onUpdateSmartList,
    filters,
    t,
    activeFilters,
    filterDocument,
    defaultFilterDocument,
    supportedFilters,
  ]);

  const filterButtonLabel = activeFilters
    ? `${t('general.labels.filter')}: ${activeFilters}`
    : t('general.labels.filter');

  return (
    <>
      {renderAsIconButton ? (
        <Styled.IconButtonContainer $isActive={!!activeFilters}>
          <IconButton
            ref={filterButtonRef}
            icon={FilterMinimalStyleCircle18}
            onClick={openFilterMenu}
            highlighted={filterMenuOpen}
            color={
              activeFilters
                ? IconButtonColors.Inherit
                : IconButtonColors.Default
            }
            preventDrag={preventDrag}
          />
          <Styled.Badge $hidden={!activeFilters}>{activeFilters}</Styled.Badge>
        </Styled.IconButtonContainer>
      ) : (
        <Button
          ref={filterButtonRef}
          onClick={openFilterMenu}
          endIcon={FilterMinimalStyleCircle18}
          size={Sizes.Small}
          variant={Variants.Text}
          color={activeFilters ? ButtonColors.Primary : ButtonColors.Inherit}
          selected={!!activeFilters}
          preventDrag={preventDrag}
        >
          {filterButtonLabel}
        </Button>
      )}

      {filterMenuOpen && (
        <FilterContextMenu
          referenceElement={filterButtonRef}
          onOutside={closeFilterMenu}
          position="bottom-end"
          title={t('context-menus.filtering.task.title')}
          options={enhancedFilters}
          activeOptions={activeOptions}
          onOption={_onFilter}
          onReset={onReset}
        />
      )}
    </>
  );
};
